import React, { useEffect, useState } from "react";
import { UserDashboardService } from "../services/user/UserDashboardService";
import { Link } from "react-router-dom";
import { ShoppingBag, Calendar, Package, ArrowRight } from "lucide-react";
import UserDashboardLayout from "../components/user/UserDashboardLayout";
import OrderDetailsModal from "../components/user/OrderDetailsModal";
import SEO from "../components/SEO";

export default function OrderHistory() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [, setError] = useState(null);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        const fetchOrders = async () => {
            try {
                const response = await UserDashboardService.getUserOrders();
                if (response.status === "success") {
                    setOrders(response.orders);
                }
            } catch (error) {
                console.error("Failed to fetch order history:", error);
                setError("Unable to load your order history at this time.");
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, []);

    const handleViewOrder = (orderId) => {
        setSelectedOrderId(orderId);
        setIsModalOpen(true);
    };

    return (
        <UserDashboardLayout title="Order History" subtitle="A complete record of your transactions and digital library.">
            <SEO title="Order History" url="/dashboard/purchase-history" />
            <section id="orders" className="bg-white rounded-2xl shadow-sm border border-black/5 p-8">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-serif italic text-black flex items-center gap-3">
                        <ShoppingBag className="text-jazz-brown" />
                        Transactions
                    </h2>
                </div>

                {loading ? (
                    <div className="space-y-4">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-[#fafafa] rounded-2xl border border-black/5 p-6 animate-pulse">
                                <div className="flex flex-wrap items-center gap-4 sm:gap-8">
                                    <div className="w-12 h-12 bg-white rounded-xl border border-black/5" />
                                    <div className="flex-1 space-y-3">
                                        <div className="h-4 w-32 bg-white rounded" />
                                        <div className="h-3 w-48 bg-white rounded" />
                                    </div>
                                    <div className="flex items-center gap-8 ml-auto">
                                        <div className="w-20 h-10 bg-gray-200 rounded" />
                                        <div className="w-32 h-12 bg-gray-200 rounded-xl" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : orders.length > 0 ? (
                    <div className="space-y-4">
                        {orders.map((order) => (
                            <div
                                key={order.id}
                                className="bg-[#fafafa] rounded-2xl border border-black/5 p-6 hover:border-jazz-brown/30 transition-all group"
                            >
                                <div className="flex flex-wrap items-center gap-4 sm:gap-8">
                                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-black/40 group-hover:bg-jazz-brown/10 group-hover:text-jazz-brown transition-colors border border-black/5 shadow-sm">
                                        <ShoppingBag size={24} />
                                    </div>

                                    <div className="flex-1 min-w-[200px]">
                                        <div className="flex items-center gap-3 mb-1">
                                            <span className="text-sm font-bold text-black font-mono tracking-tight">{order.reference}</span>
                                            <span className={`px-2 py-0.5 rounded text-[8px] font-bold tracking-[1px] uppercase ${order.status === 'paid' || order.status === 'completed' ? 'bg-green-100 text-green-700' :
                                                order.status === 'shipped' ? 'bg-blue-100 text-blue-700' :
                                                    order.status === 'pending' || order.status === 'processing' ? 'bg-amber-100 text-amber-700' :
                                                        order.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-black/5 text-black/80'
                                                }`}>
                                                {order.status}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-6">
                                            <div className="flex items-center gap-1.5 text-black/90 font-semibold">
                                                <Calendar size={12} />
                                                <span className="text-[10px] font-bold tracking-[1px] uppercase">
                                                    {new Date(order.created_at).toLocaleDateString()}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-1.5 text-black/90 font-semibold">
                                                <Package size={12} />
                                                <span className="text-[10px] font-bold tracking-[1px] uppercase">
                                                    {order.item_count} {order.item_count === 1 ? 'Item' : 'Items'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-8 ml-auto">
                                        <div className="text-right">
                                            <p className="text-[9px] tracking-[1px] uppercase text-black/90 font-bold mb-0.5">Total Amount</p>
                                            <p className="text-lg font-bold text-black">${order.amount.toFixed(2)}</p>
                                        </div>
                                        <button
                                            onClick={() => handleViewOrder(order.id)}
                                            className="py-3 px-6 bg-black text-white font-bold text-[10px] tracking-[2px] uppercase rounded-xl hover:bg-jazz-brown transition-colors flex items-center gap-2"
                                        >
                                            View Details
                                            <ArrowRight size={14} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 px-4 border-2 border-dashed border-black/10 rounded-xl">
                        <div className="w-16 h-16 bg-black/5 rounded-full flex items-center justify-center mx-auto mb-4 text-black/40">
                            <ShoppingBag size={24} />
                        </div>
                        <h3 className="text-base font-bold text-black mb-2">No orders yet</h3>
                        <p className="text-xs text-black/60 max-w-sm mx-auto mb-6">
                            You haven't placed any orders yet. Explore the shop to find music and official merchandise.
                        </p>
                        <Link
                            to="/shop"
                            className="inline-flex py-3 px-6 bg-black text-white font-bold text-[10px] tracking-[2px] uppercase rounded-xl hover:bg-jazz-brown transition-colors"
                        >
                            Start Shopping
                        </Link>
                    </div>
                )}
            </section>

            <OrderDetailsModal
                orderId={selectedOrderId}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                fetchDetails={UserDashboardService.getOrderDetails}
            />
        </UserDashboardLayout>
    );
}